import React from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import Box from '../components/Box'
import LoadingProgress from '../components/LoadingProgress'
import ErrorMessage from '../components/ErrorMessage'
import { fetchPersistedSalesArqueos, fetchPersistedOperationalArqueos, generateArqueosRange } from '../actions/arqueos'

class ArqueosDashboardView extends React.Component {
  constructor(props) {
    super(props)
    const today = new Date()
    const prior = new Date()
    prior.setDate(today.getDate() - 13) // default 14-day window
    this.state = {
      startDate: prior.toISOString().slice(0, 10),
      endDate: today.toISOString().slice(0, 10),
      loading: false,
      generateLoading: false,
      lastResult: null,
      dayStatuses: {} // map ISO date -> { status: 'pending'|'ok'|'failed', message }
    }
    // synchronous guard — prevents concurrent invocations even before setState propagates
    this._generating = false
  }

  componentDidMount() {
    this.fetchAll()
  }

  fetchAll = () => {
    const { startDate, endDate } = this.state
    this.setState({ loading: true })
    Promise.all([
      this.props.fetchPersistedSalesArqueos(startDate, endDate),
      this.props.fetchPersistedOperationalArqueos(startDate, endDate)
    ])
      .then(() => this.setState({ loading: false }))
      .catch(() => this.setState({ loading: false }))
  }

  // generate per-day sequentially to avoid lambda timeout on wide ranges
  generateMissing = async () => {
    // synchronous guard: prevents concurrent invocations while setState propagates
    if (this._generating) return
    this._generating = true

    try {
    const { startDate, endDate } = this.state
    const start = new Date(startDate)
    const end = new Date(endDate)
    const days = []
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      days.push(new Date(d))
    }

    // initialize statuses
    const dayStatuses = {}
    days.forEach(d => { dayStatuses[d.toISOString().slice(0,10)] = { status: 'pending', message: '' } })
    this.setState({ generateLoading: true, dayStatuses, lastResult: null })

    for (const d of days) {
      const iso = d.toISOString().slice(0,10)
      try {
        // update status -> running
        this.setState(prev => ({ dayStatuses: { ...prev.dayStatuses, [iso]: { status: 'running', message: 'In progress' } } }))

        // call generation for single day (server-side action expects StartDate/EndDate)
        // generateArqueosRange returns a promise (dispatchable action)
        // we await the dispatch result to proceed sequentially
        // use the same action but with StartDate=EndDate=iso
        // note: action expects ISO strings
        // eslint-disable-next-line no-await-in-loop
        const res = await this.props.generateArqueosRange(iso, iso)

        // mark ok
        this.setState(prev => ({ dayStatuses: { ...prev.dayStatuses, [iso]: { status: 'ok', message: 'OK' } }, lastResult: res }))
      } catch (err) {
        // mark failed and continue
        this.setState(prev => ({ dayStatuses: { ...prev.dayStatuses, [iso]: { status: 'failed', message: (err && err.message) || 'Error' } } }))
      }
    }

    // finished
    this.setState({ generateLoading: false })
    // refresh generated arqueos after whole run
    this.fetchAll()
    } finally {
      this._generating = false
    }
  }

  dayStatus(dayISO) {
    const { generatedSalesArqueos, generatedOperationalArqueos } = this.props
    const salesPresent = Array.isArray(generatedSalesArqueos) && generatedSalesArqueos.some(s => (s.Day || '').slice(0, 10) === dayISO)
    const opPresent = Array.isArray(generatedOperationalArqueos) && generatedOperationalArqueos.some(o => (o.Day || '').slice(0, 10) === dayISO)

    if (salesPresent && opPresent) return { color: 'green', label: 'OK' }
    if (salesPresent || opPresent) return { color: 'orange', label: 'Partial' }
    return { color: 'red', label: 'Missing' }
  }

  renderCalendar() {
    const { startDate, endDate } = this.state
    const start = new Date(startDate)
    const end = new Date(endDate)
    const days = []
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      days.push(new Date(d))
    }

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        {days.map(d => {
          const iso = d.toISOString().slice(0, 10)
          const statusFromState = this.state.dayStatuses && this.state.dayStatuses[iso]
          const status = statusFromState ? statusFromState : this.dayStatus(iso)
          const color = status.status === 'ok' ? 'green' : (status.status === 'partial' || status.status === 'running' ? 'orange' : (status.status === 'failed' ? 'red' : (status.color || 'red')))
          const label = status.status === 'running' ? (<><i className="fas fa-spinner fa-spin"></i> Generating</>) : (status.status === 'ok' ? 'OK' : (status.status === 'failed' ? 'Failed' : status.label))
          return (
            <div key={iso} style={{ width: 150, margin: 6 }}>
              <div style={{ padding: 8, borderRadius: 4, background: '#fff', border: '1px solid #ddd' }}>
                <div style={{ fontSize: 12, color: '#666' }}>{iso}</div>
                <div style={{ marginTop: 8, fontWeight: '600', color }}>{label}</div>
                {status.message && <div style={{ fontSize: 11, color: '#666', marginTop: 6 }}>{status.message}</div>}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  render() {
    const { loading, startDate, endDate, generateLoading, lastResult } = this.state
    const { error, generatedSalesArqueos, generatedOperationalArqueos, role } = this.props

    if (role !== 'ADMIN') {
      return <ErrorMessage message="Not authorized" />
    }

    return (
      <div>
        <h3>Arqueos dashboard (admin) 🔧</h3>

        <Box>
          {generateLoading ? (
            <div className="alert alert-info d-flex align-items-center" role="status">
              <i className="fas fa-spinner fa-spin mr-2" /> Generating arqueos for the selected range. The form is hidden until the process finishes.
            </div>
          ) : (
            <div className="form-row">
              <div className="col-md-3 mb-2">
                <label>Start date</label>
                <input type="date" className="form-control" value={startDate}
                  onChange={e => this.setState({ startDate: e.target.value })} />
              </div>
              <div className="col-md-3 mb-2">
                <label>End date</label>
                <input type="date" className="form-control" value={endDate}
                  onChange={e => this.setState({ endDate: e.target.value })} />
              </div>
              <div className="col-md-6 mb-2" style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button className="btn bg-vw-dark text-white mr-2" onClick={this.fetchAll} disabled={loading || generateLoading}>
                  {loading ? 'Loading...' : 'Fetch generated arqueos'}
                </button>
                <button className="btn btn-outline-primary" onClick={this.generateMissing} disabled={generateLoading}>
                  {generateLoading ? 'Generating...' : 'Generate range (call lambda)'}
                </button>
              </div>
            </div>
          )}

          <hr />

          {error && <ErrorMessage message={error.message} />}

          {loading ? <LoadingProgress /> : (
            <>
              <h5>Calendar</h5>
              {this.renderCalendar()}

              <div style={{ marginTop: 16 }}>
                <strong>Sales records:</strong> {Array.isArray(generatedSalesArqueos) ? generatedSalesArqueos.length : 0}
                {' — '}
                <strong>Operational records:</strong> {Array.isArray(generatedOperationalArqueos) ? generatedOperationalArqueos.length : 0}
              </div>

              {lastResult && (
                <div className="alert alert-info mt-3">
                  <strong>Generate result:</strong>
                  <pre style={{ maxHeight: 200, overflow: 'auto' }}>{JSON.stringify(lastResult, null, 2)}</pre>
                </div>
              )}
            </>
          )}
        </Box>

      </div>
    )
  }
}

const mapStateToProps = state => ({
  generatedSalesArqueos: state.arqueos.generatedSalesArqueos,
  generatedOperationalArqueos: state.arqueos.generatedOperationalArqueos,
  error: state.arqueos.error,
  auth: state.auth,
  role: state.auth.role
})

const mapDispatchToProps = dispatch => bindActionCreators({ fetchPersistedSalesArqueos, fetchPersistedOperationalArqueos, generateArqueosRange }, dispatch)

export default connect(mapStateToProps, mapDispatchToProps)(ArqueosDashboardView)
